import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { listAiModels } from '../../api/chat';
import type { AiModel } from '../../api/chat';
import Button from '../../components/BaseComponents/Button';
import TextInput from '../../components/BaseComponents/TextInput';
import {
  CmsAsset,
  CmsAuthor,
  CmsPostDetail,
  CmsSharedItem,
  CmsTopic,
  clearCmsFlag,
  cmsErrorMessage,
  createCmsAsset,
  createCmsAuthor,
  createCmsTopic,
  discardCmsTopic,
  dismissCmsTopic,
  generateCmsTopic,
  importCmsTopics,
  listCmsAssets,
  listCmsAuthors,
  listCmsPosts,
  listCmsReferences,
  listCmsSnippets,
  listCmsTopics,
  listCmsVariables,
  patchCmsPost,
  saveCmsSnippet,
  saveCmsVariable,
  transitionCmsPost,
} from '../../api/marketingCms';

const JSON_LD_TYPES = ['Article', 'BlogPosting', 'FAQPage', 'MedicalWebPage'];

type ArticleBlock = {
  id?: string;
  type?: string;
  text?: string;
  level?: number;
  items?: string[];
  style?: string;
  headers?: string[];
  rows?: string[][];
  caption?: string;
  question?: string;
  answer?: string;
  statement?: string;
  attribution?: string;
  heading?: string;
  body?: string;
  button_label?: string;
  name?: string;
  price_display?: string;
  strapline?: string;
  alt?: string;
  data?: { label?: string; value?: number }[];
};

const articleBlocks = (post: CmsPostDetail): ArticleBlock[] => {
  const blocks = post.content?.blocks;
  if (!Array.isArray(blocks)) {
    return [];
  }
  return blocks as ArticleBlock[];
};

const ArticleBlockView = (props: { block: ArticleBlock }) => {
  const block = props.block;
  if (block.type === 'heading') {
    return block.level === 3 ? <h4>{block.text}</h4> : <h3>{block.text}</h3>;
  }
  if (block.type === 'list') {
    const items = block.items ?? [];
    if (block.style === 'ordered') {
      return (
        <ol>
          {items.map((item, index) => (
            <li key={`${block.id ?? 'list'}-${index}`}>{item}</li>
          ))}
        </ol>
      );
    }
    return (
      <ul>
        {items.map((item, index) => (
          <li key={`${block.id ?? 'list'}-${index}`}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'table') {
    return (
      <table>
        <thead>
          <tr>
            {(block.headers ?? []).map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(block.rows ?? []).map((row, index) => (
            <tr key={`${block.id ?? 'row'}-${index}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${block.id ?? 'cell'}-${index}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (block.type === 'faq') {
    return (
      <div>
        <h4>{block.question}</h4>
        <p>{block.answer}</p>
      </div>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote>
        <p>{block.text}</p>
        <p>{block.attribution}</p>
      </blockquote>
    );
  }
  if (block.type === 'key_fact') {
    return <p>{block.statement}</p>;
  }
  if (block.type === 'callout' || block.type === 'paragraph' || block.type === 'snippet') {
    return <p>{block.text || block.body || block.statement}</p>;
  }
  if (block.type === 'cta') {
    return (
      <div>
        <h4>{block.heading}</h4>
        <p>{block.body}</p>
        <p>{block.button_label}</p>
      </div>
    );
  }
  if (block.type === 'package_card') {
    return (
      <div>
        <h4>{block.name}</h4>
        <p>{block.price_display}</p>
        <p>{block.strapline}</p>
      </div>
    );
  }
  if (block.type === 'chart') {
    return (
      <ul>
        {(block.data ?? []).map((point) => (
          <li key={point.label}>{point.label}: {point.value}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'image' || block.type === 'video') {
    return <p>{block.caption || block.alt || block.type}</p>;
  }
  if (block.type === 'divider') {
    return <hr />;
  }
  return <p>{block.text || block.body || block.type}</p>;
};

const GeneratedArticle = (props: { post: CmsPostDetail }) => {
  const blocks = articleBlocks(props.post);
  return (
    <article className='cms-article'>
      <p className='cms-kicker'>
        Generated article · {props.post.status} · /journal/{props.post.slug}
      </p>
      <h3>{props.post.title}</h3>
      {props.post.meta_description ? <p className='cms-dek'>{props.post.meta_description}</p> : null}
      {blocks.length === 0 ? (
        <p>This draft has no blocks yet.</p>
      ) : (
        blocks.map((block, index) => (
          <ArticleBlockView block={block} key={block.id || String(index)} />
        ))
      )}
    </article>
  );
};

const confirmSharedSave = async (key: string, kind: string) => {
  const pages = await listCmsReferences(key, kind);
  const live = pages.filter((page) => page.status === 'published');
  if (live.length === 0) {
    return true;
  }
  const names = live.map((page) => page.slug).join(', ');
  return window.confirm(`Saving ${key} rebuilds these live pages: ${names}`);
};

const EditorialCms = (props: { section: 'topics' | 'posts' | 'shared' }) => {
  const [topics, setTopics] = useState<CmsTopic[]>([]);
  const [csv, setCsv] = useState('title,cluster,research_doc\n');
  const [manualTitle, setManualTitle] = useState('');
  const [manualCluster, setManualCluster] = useState('');
  const [manualResearch, setManualResearch] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [research, setResearch] = useState('');
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [models, setModels] = useState<AiModel[]>([]);
  const [modelKey, setModelKey] = useState('');
  const [jsonLdType, setJsonLdType] = useState('Article');
  const [posts, setPosts] = useState<CmsPostDetail[]>([]);
  const [post, setPost] = useState<CmsPostDetail | null>(null);
  const [jsonLdText, setJsonLdText] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [authors, setAuthors] = useState<CmsAuthor[]>([]);
  const [reviewerName, setReviewerName] = useState('');
  const [variables, setVariables] = useState<CmsSharedItem[]>([]);
  const [snippets, setSnippets] = useState<CmsSharedItem[]>([]);
  const [assets, setAssets] = useState<CmsAsset[]>([]);
  const [variableKey, setVariableKey] = useState('');
  const [variableValue, setVariableValue] = useState('');
  const [snippetKey, setSnippetKey] = useState('');
  const [snippetBody, setSnippetBody] = useState('');
  const [assetUrl, setAssetUrl] = useState('');
  const [assetAlt, setAssetAlt] = useState('');
  const [assetWidth, setAssetWidth] = useState('');
  const [assetHeight, setAssetHeight] = useState('');
  const [assetKind, setAssetKind] = useState('image');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (props.section === 'topics' || props.section === 'posts') {
      const [nextTopics, modelList, nextPosts, nextAuthors] = await Promise.all([
        listCmsTopics(),
        listAiModels().catch(() => null),
        listCmsPosts(),
        props.section === 'posts' ? listCmsAuthors() : Promise.resolve([]),
      ]);
      setTopics(nextTopics);
      setPosts(nextPosts);
      if (props.section === 'topics') {
        setSelectedTopicId((current) =>
          nextTopics.some((topic) => topic.id === current) ? current : nextTopics[0]?.id ?? null
        );
      }
      if (modelList) {
        const chatModels = modelList.models.filter(
          (model) =>
            model.active &&
            model.key.indexOf('transcribe') === -1 &&
            model.model_name.indexOf('transcribe') === -1
        );
        setModels(chatModels);
        setModelKey((current) => current || modelList.default_model_key || chatModels[0]?.key || '');
      }
      if (props.section === 'posts') {
        setAuthors(nextAuthors);
        setPost((current) => nextPosts.find((item) => item.id === current?.id) ?? nextPosts[0] ?? null);
      }
    }
    if (props.section === 'shared') {
      const [nextVariables, nextSnippets, nextAssets] = await Promise.all([
        listCmsVariables(),
        listCmsSnippets(),
        listCmsAssets(),
      ]);
      setVariables(nextVariables);
      setSnippets(nextSnippets);
      setAssets(nextAssets);
    }
  };

  useEffect(() => {
    void load().catch((error) => toast.error(cmsErrorMessage(error)));
    // Each section loads its own lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.section]);

  useEffect(() => {
    setJsonLdText(post?.json_ld ? JSON.stringify(post.json_ld, null, 2) : '');
    setReturnReason('');
  }, [post]);

  useEffect(() => {
    const topic = topics.find((item) => item.id === selectedTopicId);
    setResearch(topic?.research_doc ?? '');
  }, [selectedTopicId, topics]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
      await load();
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  if (props.section === 'topics') {
    const selected = topics.find((topic) => topic.id === selectedTopicId) ?? null;
    const draft = selected?.post_id != null
      ? posts.find((item) => item.id === selected.post_id) ?? null
      : null;
    return (
      <div className='cms-generate'>
        <div className='cms-editor'>
          <h2>1. Topic intake</h2>
          <p className='cms-note'>Add one topic or import a CSV. Both run the duplicate check. Maximum 25 CSV rows.</p>
          <h3>Manual text</h3>
          <TextInput name='topic_title' title='Title' value={manualTitle} onChange={(event) => setManualTitle(event.target.value)} />
          <TextInput name='topic_cluster' title='Cluster' value={manualCluster} onChange={(event) => setManualCluster(event.target.value)} />
          <p className='cms-note'>Research document</p>
          <textarea className='cms-json' value={manualResearch} onChange={(event) => setManualResearch(event.target.value)} />
          <Button
            text='Check duplicates'
            isDisabled={busy || manualTitle.trim().length === 0}
            onClick={() => void run(async () => {
              const created = await createCmsTopic({
                title: manualTitle.trim(),
                cluster: manualCluster.trim(),
                research_doc: manualResearch.trim(),
              });
              setManualTitle('');
              setManualResearch('');
              setSelectedTopicId(created.id);
              toast.success(created.status === 'held_duplicate' ? 'Held as a duplicate' : 'Duplicate check cleared');
            })}
          />
          <h3>CSV</h3>
          <textarea className='cms-json' value={csv} onChange={(event) => setCsv(event.target.value)} />
          <Button
            text='Import CSV'
            isDisabled={busy}
            onClick={() => void run(async () => {
              await importCmsTopics(csv);
            })}
          />
        </div>
        <div className='cms-editor'>
          <h2>2. Generate draft</h2>
          {draft ? (
            <GeneratedArticle post={draft} />
          ) : (
            <p className='cms-note'>Generate a draft and the article shows here.</p>
          )}
          <div className='cms-list'>
            {topics.map((topic) => (
              <button
                type='button'
                key={topic.id}
                className={selected?.id === topic.id ? 'active' : ''}
                onClick={() => setSelectedTopicId(topic.id)}
              >
                <strong>{topic.title}</strong>
                <span className='cms-meta'>
                  {topic.status === 'held_duplicate' ? 'Held as duplicate' : topic.status === 'generated' ? 'Draft saved' : 'Dedup cleared'}
                </span>
              </button>
            ))}
          </div>
          <label className='cms-note'>
            Model
            <select className='cms-select' value={modelKey} onChange={(event) => setModelKey(event.target.value)}>
              {models.map((model) => (
                <option key={model.key} value={model.key}>
                  {model.name}{model.default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className='cms-note'>
            JSON-LD type
            <select className='cms-select' value={jsonLdType} onChange={(event) => setJsonLdType(event.target.value)}>
              {JSON_LD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
          {selected ? (
            <>
              <p className='cms-meta'>
                {selected.title}
                {' · '}
                {selected.status === 'held_duplicate' ? 'Held as duplicate' : selected.status === 'generated' ? 'Draft saved' : 'Dedup cleared'}
                {selected.duplicate_title ? ` · matches ${selected.duplicate_title}` : ''}
              </p>
              <div className='cms-regenerate'>
                <h3>Regenerate</h3>
                <p className='cms-note'>
                  {selected.status === 'generated'
                    ? 'Rewrites this draft with the model and prompt below. The live page stays up until you publish again.'
                    : 'Generate a draft first. Regenerate then replaces that same draft.'}
                </p>
                <p className='cms-note'>Additional prompt</p>
                <textarea className='cms-json' rows={3} value={additionalPrompt} onChange={(event) => setAdditionalPrompt(event.target.value)} placeholder='Optional. Added for this run only.' />
                <div className='cms-actions'>
                  {selected.status === 'cleared' ? (
                    <Button
                      text='Generate and save draft'
                      isDisabled={busy}
                      onClick={() => void run(async () => {
                        await generateCmsTopic(selected.id, {
                          json_ld_type: jsonLdType,
                          model_key: modelKey,
                          additional_prompt: additionalPrompt,
                          research_doc: research,
                        });
                        toast.success('Draft saved. Use Regenerate to rewrite it.');
                      })}
                    />
                  ) : null}
                  <Button
                    text='Regenerate'
                    isDisabled={busy || selected.status !== 'generated'}
                    onClick={() => void run(async () => {
                      await generateCmsTopic(selected.id, {
                        json_ld_type: jsonLdType,
                        model_key: modelKey,
                        additional_prompt: additionalPrompt,
                        research_doc: research,
                      });
                      toast.success('Draft regenerated');
                    })}
                  />
                  {selected.status === 'held_duplicate' ? (
                    <>
                      <Button
                        text='Clear duplicate'
                        isDisabled={busy}
                        onClick={() => void run(async () => {
                          await dismissCmsTopic(selected.id);
                        })}
                      />
                      <Button
                        text='Discard'
                        variant='delete'
                        isDisabled={busy}
                        onClick={() => void run(async () => {
                          await discardCmsTopic(selected.id);
                        })}
                      />
                    </>
                  ) : null}
                </div>
              </div>
              <p className='cms-note'>Research document</p>
              <textarea className='cms-json' rows={4} value={research} onChange={(event) => setResearch(event.target.value)} />
            </>
          ) : (
            <p className='cms-note'>Add a topic. The model list is ready for the generate step.</p>
          )}
        </div>
      </div>
    );
  }

  if (props.section === 'posts') {
    const linkedTopic = post ? topics.find((topic) => topic.post_id === post.id) ?? null : null;
    return (
      <div className='cms-layout'>
        <div className='cms-list'>
          {posts.map((item) => (
            <button
              type='button'
              key={item.id}
              className={post?.id === item.id ? 'active' : ''}
              onClick={() => setPost(item)}
            >
              <strong>{item.title}</strong>
              <span className='cms-meta'>{item.status} · {item.json_ld_type}</span>
            </button>
          ))}
        </div>
        {post ? (
          <div className='cms-editor'>
            <GeneratedArticle post={post} />
            <p className='cms-meta'>{post.workflow_status} · reviewer {post.reviewer?.name ?? 'unassigned'}</p>
            {linkedTopic ? (
              <div className='cms-regenerate'>
                <h3>Regenerate</h3>
                <p className='cms-note'>Rewrites this draft. Pick a model or add a prompt, then regenerate.</p>
                <label className='cms-note'>
                  Model
                  <select className='cms-select' value={modelKey} onChange={(event) => setModelKey(event.target.value)}>
                    {models.map((model) => (
                      <option key={model.key} value={model.key}>
                        {model.name}{model.default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <p className='cms-note'>Additional prompt</p>
                <textarea className='cms-json' rows={3} value={additionalPrompt} onChange={(event) => setAdditionalPrompt(event.target.value)} />
                <Button
                  text='Regenerate'
                  isDisabled={busy}
                  onClick={() => void run(async () => {
                    await generateCmsTopic(linkedTopic.id, {
                      json_ld_type: post.json_ld_type,
                      model_key: modelKey,
                      additional_prompt: additionalPrompt,
                      research_doc: linkedTopic.research_doc,
                    });
                    toast.success('Draft regenerated');
                  })}
                />
              </div>
            ) : (
              <p className='cms-note'>This post was not generated from a topic, so it cannot be regenerated.</p>
            )}
            <label className='cms-note'>
              JSON-LD type
              <select
                className='cms-select'
                value={post.json_ld_type}
                onChange={(event) => setPost({ ...post, json_ld_type: event.target.value })}
              >
                {JSON_LD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <textarea className='cms-json' value={jsonLdText} onChange={(event) => setJsonLdText(event.target.value)} />
            <div className='cms-actions'>
              <Button
                text='Save JSON-LD'
                isDisabled={busy}
                onClick={() => void run(async () => {
                  const parsed: unknown = jsonLdText.trim() ? JSON.parse(jsonLdText) : {};
                  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                    throw new Error('JSON-LD must be an object');
                  }
                  const saved = await patchCmsPost(post.id, {
                    json_ld_type: post.json_ld_type,
                    json_ld: parsed as Record<string, unknown>,
                  });
                  setPost(saved);
                })}
              />
            </div>
            {post.open_flags.map((flag) => (
              <div className='cms-actions' key={flag.id}>
                <span className='cms-note'>{flag.message}</span>
                <Button
                  text='Clear flag'
                  isDisabled={busy}
                  onClick={() => void run(async () => {
                    setPost(await clearCmsFlag(post.id, flag.id));
                  })}
                />
              </div>
            ))}
            <TextInput
              name='reviewer'
              title='New reviewer name'
              value={reviewerName}
              onChange={(event) => setReviewerName(event.target.value)}
            />
            <div className='cms-actions'>
              <Button
                text='Add reviewer'
                isDisabled={busy || reviewerName.trim().length === 0}
                onClick={() => void run(async () => {
                  const author = await createCmsAuthor(reviewerName.trim());
                  setReviewerName('');
                  setPost(await patchCmsPost(post.id, { reviewer_id: author.id }));
                })}
              />
              {authors.map((author) => (
                <Button
                  key={author.id}
                  text={author.name}
                  isSecondary={false}
                  isDisabled={busy}
                  onClick={() => void run(async () => {
                    setPost(await patchCmsPost(post.id, { reviewer_id: author.id }));
                  })}
                />
              ))}
            </div>
            <TextInput
              name='return_reason'
              title='Return reason'
              value={returnReason}
              onChange={(event) => setReturnReason(event.target.value)}
            />
            <div className='cms-actions'>
              {post.actions.map((action) => (
                <Button
                  key={action}
                  text={action.split('_').join(' ')}
                  isDisabled={busy}
                  onClick={() => void run(async () => {
                    setPost(await transitionCmsPost(post.id, action, returnReason));
                  })}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className='cms-layout'>
      <div className='cms-editor'>
        <h2>Variables</h2>
        {variables.map((item) => (
          <p className='cms-meta' key={item.key}>{item.key}: {item.value}</p>
        ))}
        <TextInput name='variable_key' title='Key' value={variableKey} onChange={(event) => setVariableKey(event.target.value)} />
        <TextInput name='variable_value' title='Value' value={variableValue} onChange={(event) => setVariableValue(event.target.value)} />
        <Button
          text='Save variable'
          isDisabled={busy}
          onClick={() => void run(async () => {
            if (!(await confirmSharedSave(variableKey, 'variable'))) return;
            await saveCmsVariable(variableKey, variableValue);
          })}
        />
        <h2>Snippets</h2>
        {snippets.map((item) => (
          <p className='cms-meta' key={item.key}>{item.key}</p>
        ))}
        <TextInput name='snippet_key' title='Key' value={snippetKey} onChange={(event) => setSnippetKey(event.target.value)} />
        <textarea className='cms-json' value={snippetBody} onChange={(event) => setSnippetBody(event.target.value)} />
        <Button
          text='Save snippet'
          isDisabled={busy}
          onClick={() => void run(async () => {
            if (!(await confirmSharedSave(snippetKey, 'snippet'))) return;
            await saveCmsSnippet(snippetKey, snippetBody);
          })}
        />
      </div>
      <div className='cms-editor'>
        <h2>Assets</h2>
        {assets.map((asset) => (
          <p className='cms-meta' key={asset.id}>
            {asset.kind} #{asset.id}
            {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
            {' · '}
            {asset.url}
          </p>
        ))}
        <label className='cms-note'>
          Kind
          <select value={assetKind} onChange={(event) => setAssetKind(event.target.value)}>
            <option value='image'>image</option>
            <option value='video'>video</option>
            <option value='file'>file</option>
          </select>
        </label>
        <TextInput name='asset_url' title='URL' value={assetUrl} onChange={(event) => setAssetUrl(event.target.value)} />
        <TextInput name='asset_alt' title='Alt text' value={assetAlt} onChange={(event) => setAssetAlt(event.target.value)} />
        <TextInput name='asset_width' title='Width' value={assetWidth} onChange={(event) => setAssetWidth(event.target.value)} />
        <TextInput name='asset_height' title='Height' value={assetHeight} onChange={(event) => setAssetHeight(event.target.value)} />
        <Button
          text='Add asset'
          isDisabled={busy}
          onClick={() => void run(async () => {
            const dimension = (value: string) => {
              const parsed = Number(value);
              return value.trim().length > 0 && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
            };
            await createCmsAsset({
              kind: assetKind,
              filename: assetUrl.split('/').pop() || 'asset',
              mime_type: assetKind === 'video' ? 'video/mp4' : 'image/jpeg',
              url: assetUrl,
              alt: assetAlt,
              credit: '',
              width: dimension(assetWidth),
              height: dimension(assetHeight),
            });
          })}
        />
      </div>
    </div>
  );
};

export default EditorialCms;
